<script setup>
/* global axios */
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const accountId = computed(() => getters['getCurrentAccountId'].value);
const agents = useMapGetter('agents/getAgents');

const conversations = ref([]);
const allOpenConversations = ref([]);
const isLoading = ref(true);
const lastUpdated = ref(null);
let refreshTimer = null;

const REFRESH_INTERVAL = 30000;

const fetchConversations = async () => {
  try {
    // Fetch all open conversations (assigned + unassigned) para tener contexto completo
    const [assignedRes, allRes] = await Promise.all([
      axios.get(`/api/v1/accounts/${accountId.value}/conversations`, {
        params: { status: 'open', assignee_type: 'assigned', page: 1 },
      }),
      axios.get(`/api/v1/accounts/${accountId.value}/conversations`, {
        params: { status: 'open', page: 1 },
      }),
    ]);
    conversations.value = assignedRes.data?.data?.payload || [];
    allOpenConversations.value = allRes.data?.data?.payload || [];
    lastUpdated.value = new Date();
  } catch (e) {
    // silently fail, keep last data
  } finally {
    isLoading.value = false;
  }
};

// Estado reactivo directo del store de escritura
const typingStatusRecords = computed(
  () => store.state.conversationTypingStatus.records
);

// Feed global de agentes escribiendo AHORA (cualquier conversación)
const activeTypingFeed = computed(() => {
  const feed = [];
  Object.entries(typingStatusRecords.value).forEach(([convId, users]) => {
    const agentUsers = (users || []).filter(u => u.type === 'user');
    agentUsers.forEach(user => {
      const conv =
        allOpenConversations.value.find(c => c.id === Number(convId)) ||
        conversations.value.find(c => c.id === Number(convId));
      feed.push({
        convId: Number(convId),
        user,
        contactName: conv?.meta?.sender?.name || null,
        assigneeName: conv?.meta?.assignee?.name || null,
      });
    });
  });
  return feed;
});

const getTypingUsers = conversationId => {
  return typingStatusRecords.value[Number(conversationId)] || [];
};

// Devuelve los eventos de escritura de un agente específico (por su id)
const getAgentTypingEvents = agentId => {
  return activeTypingFeed.value.filter(item => item.user.id === agentId);
};

const statusClass = status => {
  if (status === 'online') return 'bg-green-500';
  if (status === 'busy') return 'bg-yellow-400';
  return 'bg-n-slate-5';
};

const statusLabel = status => {
  if (status === 'online') return t('AGENT_MONITOR.STATUS.ONLINE');
  if (status === 'busy') return t('AGENT_MONITOR.STATUS.BUSY');
  return t('AGENT_MONITOR.STATUS.OFFLINE');
};

const timeAgo = timestamp => {
  if (!timestamp) return '';
  const diff = Math.floor(Date.now() / 1000 - timestamp);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h`;
};

const agentRows = computed(() => {
  const map = {};

  agents.value.forEach(agent => {
    map[agent.id] = { agent, conversations: [] };
  });

  conversations.value.forEach(conv => {
    const assignee = conv.meta?.assignee;
    if (assignee?.id && map[assignee.id]) {
      map[assignee.id].conversations.push(conv);
    }
  });

  return Object.values(map).sort((a, b) => {
    if (b.conversations.length !== a.conversations.length) {
      return b.conversations.length - a.conversations.length;
    }
    const order = { online: 0, busy: 1, offline: 2 };
    const aOrder = order[a.agent.availability_status] ?? 2;
    const bOrder = order[b.agent.availability_status] ?? 2;
    return aOrder - bOrder;
  });
});

const unassigned = computed(() =>
  conversations.value.filter(c => !c.meta?.assignee)
);

const lastUpdatedLabel = computed(() => {
  if (!lastUpdated.value) return '';
  return lastUpdated.value.toLocaleTimeString();
});

onMounted(() => {
  store.dispatch('agents/get');
  fetchConversations();
  refreshTimer = setInterval(fetchConversations, REFRESH_INTERVAL);
});

onUnmounted(() => {
  clearInterval(refreshTimer);
});
</script>

<template>
  <div class="flex flex-col h-full overflow-y-auto p-6 bg-n-background">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('AGENT_MONITOR.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-10 mt-0.5">
          {{ t('AGENT_MONITOR.SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-3">
        <span v-if="lastUpdatedLabel" class="text-xs text-n-slate-9">
          {{ t('AGENT_MONITOR.LAST_UPDATED') }}: {{ lastUpdatedLabel }}
        </span>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-11 transition-colors"
          @click="fetchConversations"
        >
          <span class="i-lucide-refresh-cw h-3.5 w-3.5" />
          {{ t('AGENT_MONITOR.REFRESH') }}
        </button>
      </div>
    </div>

    <!-- Stats rápidas -->
    <div class="grid grid-cols-4 gap-3 mb-6">
      <div class="bg-n-solid-1 rounded-xl p-4 border border-n-weak">
        <p class="text-xs text-n-slate-10 mb-1">{{ t('AGENT_MONITOR.STATS.TOTAL_OPEN') }}</p>
        <p class="text-2xl font-bold text-n-slate-12">{{ conversations.length }}</p>
      </div>
      <div class="bg-n-solid-1 rounded-xl p-4 border border-n-weak">
        <p class="text-xs text-n-slate-10 mb-1">{{ t('AGENT_MONITOR.STATS.ONLINE_AGENTS') }}</p>
        <p class="text-2xl font-bold text-green-500">
          {{ agents.filter(a => a.availability_status === 'online').length }}
        </p>
      </div>
      <div class="bg-n-solid-1 rounded-xl p-4 border border-n-weak">
        <p class="text-xs text-n-slate-10 mb-1">{{ t('AGENT_MONITOR.STATS.BUSY_AGENTS') }}</p>
        <p class="text-2xl font-bold text-yellow-400">
          {{ agents.filter(a => a.availability_status === 'busy').length }}
        </p>
      </div>
      <div class="bg-n-solid-1 rounded-xl p-4 border border-n-weak">
        <p class="text-xs text-n-slate-10 mb-1">{{ t('AGENT_MONITOR.STATS.UNASSIGNED') }}</p>
        <p class="text-2xl font-bold text-red-400">{{ unassigned.length }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-20 text-n-slate-10">
      <span class="i-lucide-loader-2 h-6 w-6 animate-spin mr-2" />
      {{ t('AGENT_MONITOR.LOADING') }}
    </div>

    <!-- Tabla de agentes -->
    <div v-else class="bg-n-solid-1 rounded-xl border border-n-weak overflow-hidden">
      <table class="min-w-full">
        <thead>
          <tr class="border-b border-n-weak">
            <th class="text-left py-3 px-4 text-xs font-semibold text-n-slate-10 uppercase tracking-wide">
              {{ t('AGENT_MONITOR.TABLE.AGENT') }}
            </th>
            <th class="text-left py-3 px-4 text-xs font-semibold text-n-slate-10 uppercase tracking-wide">
              {{ t('AGENT_MONITOR.TABLE.STATUS') }}
            </th>
            <th class="text-left py-3 px-4 text-xs font-semibold text-n-slate-10 uppercase tracking-wide">
              {{ t('AGENT_MONITOR.TYPING_NOW.TITLE') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="row in agentRows"
            :key="row.agent.id"
            class="hover:bg-n-slate-2 transition-colors"
          >
            <!-- Agente -->
            <td class="py-3 px-4">
              <div class="flex items-center gap-3">
                <div class="relative flex-shrink-0">
                  <img
                    v-if="row.agent.avatar_url"
                    :src="row.agent.avatar_url"
                    class="h-8 w-8 rounded-full object-cover"
                    :alt="row.agent.name"
                  />
                  <div
                    v-else
                    class="h-8 w-8 rounded-full bg-n-brand flex items-center justify-center text-white text-xs font-semibold"
                  >
                    {{ row.agent.name?.charAt(0)?.toUpperCase() }}
                  </div>
                  <span
                    class="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-n-solid-1"
                    :class="statusClass(row.agent.availability_status)"
                  />
                </div>
                <div>
                  <p class="text-sm font-medium text-n-slate-12">{{ row.agent.name }}</p>
                  <p class="text-xs text-n-slate-9">{{ row.agent.email }}</p>
                </div>
              </div>
            </td>

            <!-- Estado -->
            <td class="py-3 px-4">
              <span
                class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                :class="{
                  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300': row.agent.availability_status === 'online',
                  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300': row.agent.availability_status === 'busy',
                  'bg-n-slate-3 text-n-slate-9': row.agent.availability_status === 'offline' || !row.agent.availability_status,
                }"
              >
                <span class="h-1.5 w-1.5 rounded-full" :class="statusClass(row.agent.availability_status)" />
                {{ statusLabel(row.agent.availability_status) }}
              </span>
            </td>

            <!-- Escribiendo ahora -->
            <td class="py-3 px-4">
              <div v-if="getAgentTypingEvents(row.agent.id).length > 0" class="flex flex-col gap-1">
                <div
                  v-for="item in getAgentTypingEvents(row.agent.id)"
                  :key="item.convId"
                  class="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400"
                >
                  <span class="relative flex h-2 w-2 flex-shrink-0">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </span>
                  <span class="i-lucide-pencil-line h-3 w-3 animate-pulse flex-shrink-0" />
                  <span class="font-medium">#{{ item.convId }}</span>
                  <span v-if="item.contactName" class="text-green-500 truncate max-w-[120px]">· {{ item.contactName }}</span>
                </div>
              </div>
              <span v-else class="text-xs text-n-slate-7">—</span>
            </td>

          </tr>
        </tbody>
      </table>

      <!-- Sin agentes -->
      <div v-if="agentRows.length === 0" class="py-12 text-center text-n-slate-9">
        <span class="i-lucide-users h-8 w-8 mx-auto mb-2 block" />
        {{ t('AGENT_MONITOR.EMPTY') }}
      </div>
    </div>

    <!-- Conversaciones sin asignar -->
    <div v-if="unassigned.length > 0" class="mt-4 bg-n-solid-1 rounded-xl border border-n-weak overflow-hidden">
      <div class="px-4 py-3 border-b border-n-weak flex items-center gap-2">
        <span class="i-lucide-inbox h-4 w-4 text-red-400" />
        <h2 class="text-sm font-semibold text-n-slate-11">
          {{ t('AGENT_MONITOR.UNASSIGNED.TITLE') }} ({{ unassigned.length }})
        </h2>
      </div>
      <div class="divide-y divide-n-weak">
        <div
          v-for="conv in unassigned"
          :key="conv.id"
          class="flex items-center gap-3 px-4 py-3 hover:bg-n-slate-2 transition-colors"
        >
          <span class="text-xs font-semibold text-n-brand">#{{ conv.id }}</span>
          <span class="text-sm text-n-slate-11">{{ conv.meta?.sender?.name || t('AGENT_MONITOR.TABLE.UNKNOWN_CONTACT') }}</span>
          <span class="ml-auto text-xs text-n-slate-8">{{ timeAgo(conv.last_activity_at) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
